import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";

const NETWORK_IMG = "https://images.unsplash.com/photo-1758908726551-bb2b73a7852e?w=1200&q=80";

const useCounter = (target, duration = 2000, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
};

const StatItem = ({ value, suffix = "", label, color = "#00E0FF", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const count = useCounter(parseInt(value) || 0, 1800, inView);
  const isNumeric = !isNaN(parseInt(value));

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.7, delay }}
      data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}
      className="flex flex-col"
      style={{ borderLeft: `2px solid ${color}`, paddingLeft: "28px" }}
    >
      <span
        className="font-heading font-bold"
        style={{ fontSize: "clamp(48px, 5vw, 80px)", lineHeight: 1, color }}
      >
        {isNumeric ? `+${count}` : value}
        {suffix}
      </span>
      <span
        className="font-body text-zinc-500 mt-2"
        style={{ fontSize: "12px", letterSpacing: "0.2em", textTransform: "uppercase" }}
      >
        {label}
      </span>
    </motion.div>
  );
};

const RedEdificios = () => (
  <section
    id="red"
    data-testid="red-edificios-section"
    style={{ backgroundColor: "#050505", paddingTop: "120px", paddingBottom: "120px" }}
  >
    <div className="edi-container">
      {/* Top label */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7 }}
        className="mb-16"
      >
        <span className="section-label">Cobertura Nacional</span>
        <div style={{ marginTop: "20px" }}>
          <div className="line-accent" />
        </div>
        <h2
          className="section-heading"
          style={{ fontSize: "clamp(42px, 5.5vw, 80px)", maxWidth: "700px" }}
        >
          La red de edificios<br />
          <span style={{ color: "#00E0FF" }}>más grande</span><br />
          de Chile
        </h2>
      </motion.div>

      {/* Main layout: image + stats + text */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Image */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-6 relative overflow-hidden"
          style={{ height: "480px" }}
        >
          <img
            src={NETWORK_IMG}
            alt="Red de edificios Chile"
            className="w-full h-full object-cover"
            style={{ filter: "brightness(0.7) saturate(0.8)" }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(to right, transparent 60%, #050505 100%)",
            }}
          />
          {/* Tag */}
          <div
            className="absolute bottom-8 left-8"
            style={{
              border: "1px solid rgba(0,224,255,0.2)",
              backgroundColor: "rgba(0,224,255,0.05)",
              padding: "10px 18px",
            }}
          >
            <span
              className="font-body font-bold uppercase"
              style={{ fontSize: "10px", color: "#00E0FF", letterSpacing: "0.2em" }}
            >
              Red Activa — Chile
            </span>
          </div>
        </motion.div>

        {/* Stats + text */}
        <div className="lg:col-span-6 flex flex-col gap-12">
          <div className="grid grid-cols-2 gap-8">
            <StatItem value="500" label="Edificios Residenciales" color="#00E0FF" delay={0} />
            <StatItem value="100" label="Marcas en Red" color="#D4AF37" delay={0.1} />
            <StatItem value="30" suffix=" años" label="De Experiencia" color="#00E0FF" delay={0.2} />
            <StatItem value="Todo Chile" label="Cobertura Nacional" color="#D4AF37" delay={0.3} />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "36px" }}
          >
            <p
              className="font-body text-zinc-400 mb-6"
              style={{ fontSize: "15px", lineHeight: 1.8, fontWeight: 300 }}
            >
              La red EDIMEDIA opera en <strong className="text-white">edificios residenciales</strong>, conectando{" "}
              <strong className="text-white">comunidades</strong>,{" "}
              <strong className="text-white">administradores</strong> e{" "}
              <strong className="text-white">inmobiliarias</strong> con las marcas más importantes del mercado chileno.
            </p>

            <div className="flex flex-wrap gap-3">
              {["Edificios Residenciales", "Comunidades", "Administradores", "Inmobiliarias"].map(
                (tag, i) => (
                  <span
                    key={i}
                    className="font-body font-bold uppercase"
                    style={{
                      fontSize: "10px",
                      letterSpacing: "0.18em",
                      color: "#A1A1AA",
                      border: "1px solid rgba(255,255,255,0.1)",
                      padding: "8px 14px",
                    }}
                  >
                    {tag}
                  </span>
                )
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

export { RedEdificios };
